import React from 'react'
import { Card, Flex, P, Span } from '../../lib/components'
import { InvestorRiskProfileAnswers } from '../../lib/types'
import {
  getRiskAnswerRows,
  getRiskProfileBandLabel,
  getRiskProfileSchema,
  hasRiskProfileAnswers,
} from '../../lib/utils/risk-profiler'

type InvestorRiskProfileProps = {
  completed?: boolean
  answers?: InvestorRiskProfileAnswers
}

const InvestorRiskProfile = ({ completed = false, answers }: InvestorRiskProfileProps) => {
  const hasAnswers = hasRiskProfileAnswers(answers)
  const showDetails = completed || hasAnswers

  if (!showDetails) {
    return (
      <div className='risk-profile-section mb-24' data-testid='profile-risk-profile-section'>
        <Card classes={['p-40', 'mb-16']}>
          <P classes={['mb-16']} text='Risk Profile' />
          <Span variant='thin' text='Not completed' dataTestId='profile-risk-profile-empty' />
        </Card>
      </div>
    )
  }

  const schema = getRiskProfileSchema(answers)

  const scoreLabel =
    schema === 'current' || schema === 'v15'
      ? 'Total score'
      : schema === 'legacy'
      ? 'Average score'
      : 'Score'
  const scoreValue =
    schema === 'legacy'
      ? answers?.averageScore
      : schema === 'current' || schema === 'v15'
      ? answers?.totalScore
      : answers?.totalScore ?? answers?.averageScore

  const questionnaireLabel =
    schema === 'current'
      ? 'Current (6 questions)'
      : schema === 'v15'
      ? 'Previous (15 questions)'
      : schema === 'legacy'
      ? 'Legacy (6 questions)'
      : null

  const answerRows = getRiskAnswerRows(answers?.answers, schema)

  return (
    <div className='risk-profile-section mb-24' data-testid='profile-risk-profile-section'>
      <Card classes={['p-40', 'mb-16']}>
        <P classes={['mb-16']} text='Risk Profile' />

        <Flex variant='space-between' dataTestId='profile-risk-profile-status'>
          <Span variant='bold' text='Status' />
          <Span
            variant='thin'
            text={completed ? 'Completed' : 'Not completed'}
            dataTestId='profile-risk-profile-completed'
          />
        </Flex>

        {questionnaireLabel && (
          <Flex variant='space-between' dataTestId='profile-risk-profile-schema'>
            <Span variant='bold' text='Questionnaire' />
            <Span variant='thin' text={questionnaireLabel} />
          </Flex>
        )}

        <Flex variant='space-between' dataTestId='profile-risk-profile-band'>
          <Span variant='bold' text='Risk band' />
          <Span
            variant='thin'
            text={getRiskProfileBandLabel(answers?.riskBand)}
            dataTestId='profile-risk-profile-band-value'
          />
        </Flex>

        <Flex variant='space-between' dataTestId='profile-risk-profile-score'>
          <Span variant='bold' text={scoreLabel} />
          <Span
            variant='thin'
            text={scoreValue !== undefined && scoreValue !== null ? String(scoreValue) : '—'}
            dataTestId='profile-risk-profile-score-value'
          />
        </Flex>

        {answerRows.length > 0 && (
          <div className='risk-profile-section__answers mt-16'>
            <P classes={['mb-8']} text='Answers' />
            {answerRows.map((row, index) => (
              <Flex
                key={row.questionId}
                variant='column'
                classes={['mb-12']}
                dataTestId={`profile-risk-profile-answer-${index + 1}`}
              >
                <Span variant='bold' text={row.questionLabel} />
                <Span variant='thin' text={row.optionLabel} />
              </Flex>
            ))}
          </div>
        )}
      </Card>
    </div>
  )
}

export { InvestorRiskProfile }
